import React, { useState, useEffect } from "react";

const ProductDetailModal = ({ product, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    pincode: "",
    role: "", // Add role field
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [pincodeData, setPincodeData] = useState(null);

  useEffect(() => {
    if (formData.pincode && formData.pincode.length === 6) {
      fetchPincodeData(formData.pincode);
    }
  }, [formData.pincode]);

  const fetchPincodeData = async (pincode) => {
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`,
      );
      if (response.ok) {
        const data = await response.json();
        if (data[0].Status === "Success") {
          setPincodeData(data[0].PostOffice[0]);
        } else {
          setPincodeData(null);
        }
      } else {
        setPincodeData(null);
      }
    } catch (error) {
      setPincodeData(null);
    }
  };

  if (!product) return null;

  const validatePhone = (phone) => {
    const phoneRegex = /^(?:(?:\+91)|(?:91)|(?:0))?[6-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ""));
  };

  const validatePincode = (pincode) => {
    const pincodeRegex = /^\d{6}$/;
    return pincodeRegex.test(pincode);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRoleChange = (role) => {
    setFormData((prev) => ({
      ...prev,
      role: role,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid Indian mobile number";
    }

    if (!formData.pincode) {
      newErrors.pincode = "Pincode is required";
    } else if (!validatePincode(formData.pincode)) {
      newErrors.pincode = "Please enter a valid 6-digit pincode";
    }

    if (!formData.role) {
      newErrors.role = "Please select a role";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setSubmitStatus("Please correct the errors below");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    const message = `Inquiry For ${product.title} from pincode ${formData.pincode
      }. Location: ${pincodeData ? pincodeData.Name : "Unknown"} , District: ${pincodeData ? pincodeData.District : "Unknown"
      } , State: ${pincodeData ? pincodeData.State : "Unknown"} , Country: ${pincodeData ? pincodeData.Country : "Unknown"
      } . Role: ${formData.role}`;

    try {
      const response = await fetch(
        `https://webadmin.rigve.com/api/save_contact?name=${encodeURIComponent(
          formData.name,
        )}&phone=${encodeURIComponent(
          formData.phone,
        )}&email=${encodeURIComponent(
          formData.email || "-",
        )}&msg=${encodeURIComponent(message)}`,
      );

      if (!response.ok) {
        throw new Error("Failed to submit inquiry");
      }

      setSubmitStatus("success");
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setSubmitStatus("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
      {/* Modal Container */}
      <div className="relative w-full max-w-5xl max-h-[95vh] overflow-y-auto rounded-3xl bg-white shadow-2xl border border-orange-100">
        {/* Decorative Blur Background */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-yellow-100 rounded-full blur-3xl opacity-30"></div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-20 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-gray-500 hover:text-red-500 hover:bg-red-50 transition"
        >
          ✕
        </button>

        {/* Content */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Left Side Image Section */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-400 p-6 md:p-8 flex flex-col justify-center text-white">
            <h2 className="text-2xl font-bold leading-tight mb-4">
              Premium Wall Texture Collection
            </h2>
            <p className="text-orange-100 mb-6 text-sm md:text-base">
              Transform your interiors with stylish wall texture finishes
              designed for modern homes, offices, and luxury spaces.
            </p>

            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20">
              <img
                src={product.image || "/images/wall-texture-modern.jpg"}
                alt={product.title}
                className="w-full h-[300px] md:h-[230px] object-cover hover:scale-105 transition duration-700"
              />
            </div>

            <div className="mt-6 space-y-3">
              <div className="bg-white/10 p-3 rounded-xl">✔ Elegant Finish</div>
              <div className="bg-white/10 p-3 rounded-xl">
                ✔ Premium Quality
              </div>
              <div className="bg-white/10 p-3 rounded-xl">✔ Custom Designs</div>
            </div>
          </div>

          {/* Right Side Form Section */}
          <div className="p-6 md:p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {product.title}
            </h3>

            <p className="text-gray-600 mb-4 text-sm md:text-base leading-relaxed">
              {product.description}
            </p>

            <p className="text-sm text-gray-500 mb-6">
              Product: {product.category.join(", ")}
            </p>

            {/* Form */}
            <div className="space-y-5">
              {/* Name */}
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Full Name *"
                  className={`w-full px-5 py-3 rounded-xl border bg-gray-50 focus:ring-2 focus:ring-orange-400 outline-none ${errors.name ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Mobile Number *"
                  className={`w-full px-5 py-3 rounded-xl border bg-gray-50 focus:ring-2 focus:ring-orange-400 outline-none ${errors.phone ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
              </div>

              {/* Pincode */}
              <div>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  placeholder="Pincode *"
                  className={`w-full px-5 py-3 rounded-xl border bg-gray-50 focus:ring-2 focus:ring-orange-400 outline-none ${errors.pincode ? "border-red-500" : "border-gray-300"
                    }`}
                />
                {errors.pincode && (
                  <p className="text-red-500 text-sm mt-1">{errors.pincode}</p>
                )}
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-gray-800 font-medium mb-3">
                  To serve you better, please select:
                </label>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 bg-gray-50 border rounded-xl px-4 py-3 cursor-pointer hover:border-orange-400 transition">
                    <input
                      type="radio"
                      name="role"
                      value="Architect/Interior Designer"
                      checked={formData.role === "Architect/Interior Designer"}
                      onChange={() =>
                        handleRoleChange("Architect/Interior Designer")
                      }
                      className="accent-orange-500"
                    />
                    <span className="text-gray-700">
                      Architect / Interior Designer
                    </span>
                  </label>

                  <label className="flex items-center gap-3 bg-gray-50 border rounded-xl px-4 py-3 cursor-pointer hover:border-orange-400 transition">
                    <input
                      type="radio"
                      name="role"
                      value="End User (Homeowner/Client)"
                      checked={formData.role === "End User (Homeowner/Client)"}
                      onChange={() =>
                        handleRoleChange("End User (Homeowner/Client)")
                      }
                      className="accent-orange-500"
                    />
                    <span className="text-gray-700">
                      End User (Homeowner / Client)
                    </span>
                  </label>

                  <label className="flex items-center gap-3 bg-gray-50 border rounded-xl px-4 py-3 cursor-pointer hover:border-orange-400 transition">
                    <input
                      type="radio"
                      name="role"
                      value="Dealership/Distribution"
                      checked={formData.role === "Dealership/Distribution"}
                      onChange={() =>
                        handleRoleChange("Dealership/Distribution")
                      }
                      className="accent-orange-500"
                    />
                    <span className="text-gray-700">
                      Dealership / Distribution
                    </span>
                  </label>
                </div>

                {errors.role && (
                  <p className="text-red-500 text-sm mt-1">{errors.role}</p>
                )}
              </div>

              {/* Submit Status */}
              {submitStatus && (
                <p
                  className={`text-sm ${submitStatus === "success"
                      ? "text-green-600"
                      : "text-red-600"
                    }`}
                >
                  {submitStatus === "success"
                    ? "Inquiry submitted successfully!"
                    : submitStatus}
                </p>
              )}

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button
                  onClick={onClose}
                  className="w-full sm:w-auto px-6 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-3 rounded-xl bg-orange-500 text-white hover:bg-orange-600 shadow-lg transition ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                >
                  {isSubmitting ? "Submitting..." : "Submit Inquiry"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
